import { useTranslation } from "react-i18next";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { MultiSelect } from "@tellescope/ui";
import type { MultiSelectOption } from "@tellescope/ui";
import { formatInitials, formatName } from "@tellescope/utils";
import { CareTeamMemberOption } from "./CareTeamMemberOption";

interface CareTeamMemberMultiSelectProps {
  careTeamUserIds: string[];
  value: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
  id?: string;
}

export const CareTeamMemberMultiSelect = ({
  careTeamUserIds,
  value,
  onChange,
  disabled,
  id,
}: CareTeamMemberMultiSelectProps) => {
  const { t } = useTranslation("messages");

  const usersQuery = useTellescopeQuery({
    model: "users",
    method: "getByIds",
    request: { ids: careTeamUserIds },
    enabled: careTeamUserIds.length > 0,
    select: (data) => [...(data?.matches ?? [])].sort((a, b) => formatName(a).localeCompare(formatName(b))),
  });

  const careTeamUsers = usersQuery.data ?? [];
  const isLoading = usersQuery.isLoading;

  const options: MultiSelectOption[] = careTeamUsers.map((user) => ({
    label: formatName(user) || user.email || user.id,
    value: user.id,
  }));

  const renderOption = (option: MultiSelectOption) => {
    const user = careTeamUsers.find((u) => u.id === option.value);
    return (
      <CareTeamMemberOption
        option={option}
        avatar={user?.avatar}
        initials={user ? formatInitials(user) : option.label.slice(0, 2).toUpperCase()}
      />
    );
  };

  return (
    <MultiSelect
      id={id}
      options={options}
      value={value}
      onValueChange={onChange}
      placeholder={isLoading ? t("Loading care team...") : t("Select care team members")}
      disabled={disabled || isLoading}
      isLoading={isLoading}
      renderOption={renderOption}
      maxDisplayCount={3}
    />
  );
};
